import { FC } from 'react';

import CreatorAvatar from '../CreatorAvatar';
import VideoPlaceholder from '../VideoPlaceholder';
import CtaButton from '@components/quiz/shared/CtaButton';
import { buildSanityImageSrcSet, buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityLandingHero } from '@lib/sanity/types';

const BACKGROUND_WIDTH = 1920;
const BACKGROUND_SRCSET_WIDTHS = [640, 960, 1280, 1920];
const PORTRAIT_VIDEO_MAX_WIDTH = '300px';
const LOCKUP_AVATAR_SIZE = 40;

type LandingHeroProps = {
  hero: SanityLandingHero;
  /** Where the CTA points: the pricing section anchor. */
  ctaHref: string;
};

const LandingHero: FC<LandingHeroProps> = ({ hero, ctaHref }) => {
  const {
    variant,
    eyebrow,
    heading,
    subheading,
    backgroundImage,
    ctaText,
    creatorName,
    creatorPortrait,
    creatorVideo
  } = hero;

  const isCreator = variant === 'creator';
  const hasBackground = Boolean(backgroundImage);

  const headingColour = hasBackground ? 'text-white' : 'text-landing-ink';
  const subColour = hasBackground ? 'text-white/85' : 'text-landing-slate';

  return (
    <section className="relative overflow-hidden border-b border-landing-hairline bg-white px-6 pt-14 pb-16 text-center">
      {/* The image optimiser is off site-wide, so responsive variants come
          from the Sanity CDN via srcset; the veil keeps the copy legible. */}
      {backgroundImage && (
        <>
          <img
            src={buildSanityImageUrl(backgroundImage)
              .width(BACKGROUND_WIDTH)
              .quality(80)
              .url()}
            srcSet={buildSanityImageSrcSet(
              backgroundImage,
              BACKGROUND_SRCSET_WIDTHS
            )}
            sizes="100vw"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0 bg-landing-ink/[0.62]"
            aria-hidden="true"
          />
        </>
      )}
      <div className="relative mx-auto w-full max-w-md md:max-w-2xl">
        {/* Co-brand lockup: creator × FFH. Only on the affiliate variant, and
            only once there is a creator to show. */}
        {isCreator && (creatorName || creatorPortrait) && (
          <div className="mb-7 flex items-center justify-center gap-3">
            <CreatorAvatar
              name={creatorName}
              portrait={creatorPortrait}
              renderedSize={LOCKUP_AVATAR_SIZE}
              initialTextClassName="text-sm"
            />
            <span className="text-lg font-bold text-landing-muted">×</span>
            <span
              className={`text-lg font-bold tracking-tight ${
                hasBackground ? 'text-white' : 'text-landing-ink'
              }`}
            >
              FFH
            </span>
          </div>
        )}

        {eyebrow && (
          <span
            className="cms-eyebrow mb-4 inline-block rounded-full px-3.5 py-1.5 text-[color:var(--accent)]"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent) 14%, white)'
            }}
          >
            {eyebrow}
          </span>
        )}

        <h1 className={`cms-heading ${headingColour}`}>{heading}</h1>

        {subheading && (
          <p className={`cms-subtext mx-auto mt-4 max-w-xl ${subColour}`}>
            {subheading}
          </p>
        )}

        {creatorVideo && (
          <div
            className="mx-auto mt-8"
            style={{ maxWidth: isCreator ? PORTRAIT_VIDEO_MAX_WIDTH : '100%' }}
          >
            <VideoPlaceholder video={creatorVideo} />
          </div>
        )}

        {/* The shared funnel CTA, so the hero and the paywall checkout button
            on the same page cannot drift apart in radius or colour. */}
        {ctaText && (
          <div className="mx-auto mt-8 max-w-xs">
            <CtaButton href={ctaHref}>{ctaText}</CtaButton>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingHero;
